// Insurance search as URL query values, shared by the search widget, results and checkout pages.
// Query: journey=single|student|annual, dest=ASI,TH (3-letter region or 2-letter country codes),
// start, end (single trip), cover (student days), maxTrip (annual), ages=30,35.
import { addDaysIso, daysBetween, fmtDate, parseIso, toIso } from '../../shared/data/common';
import { countries, countryName } from '../../shared/data/countries';

export const journeys = [
  ['single', 'Single trip', 'One trip, up to 180 days'],
  ['student', 'Student', 'Studying abroad, 6 months to 3 years'],
  ['annual', 'Annual multi-trip', 'Unlimited trips for a year'],
];

const journeyType = { single: 'Standalone', student: 'Student', annual: 'AnnualMultiTrip' };
export const journeyLabel = (journey) => (journeys.find(([k]) => k === journey) || journeys[0])[1];
export const journeyFromType = (type) => Object.keys(journeyType).find((k) => journeyType[k] === type) || 'single';

export const regions = [
  ['ASI', 'Asia', 'Thailand, Singapore, Bali, Dubai…'],
  ['SCH', 'Schengen', '29 Schengen countries, visa-compliant'],
  ['EUR', 'Europe', 'Including UK and non-Schengen Europe'],
  ['MDE', 'Middle East', 'UAE, Saudi Arabia, Qatar, Oman…'],
  ['USC', 'USA & Canada', 'Including transit'],
];

export const studentCover = [['180', '6 months'], ['365', '1 year'], ['730', '2 years'], ['1095', '3 years']];
export const annualTripLengths = [['30', 'Up to 30 days'], ['45', 'Up to 45 days'], ['60', 'Up to 60 days'], ['90', 'Up to 90 days']];

// India (cover is for trips abroad) and countries TripSafe does not cover.
const excluded = new Set(['IN', 'MM', 'IR', 'KP']);
export const destinationCountries = countries.filter(([code]) => !excluded.has(code));
export const popularCountries = ['TH', 'AE', 'SG', 'GB', 'US', 'FR', 'ID', 'MY', 'AU', 'CA', 'JP', 'DE'];

export const isRegion = (code) => code.length === 3;
export const destinationName = (code) => (regions.find(([c]) => c === code) || [])[1] || countryName(code);

export const ageLimits = (journey) => (journey === 'student' ? { min: 18, max: 45 } : { min: 0, max: 70 });
export const MAX_TRAVELLERS = 10;
export const MAX_TRIP_DAYS = 180;

const list = (value) => String(value || '').split(',').map((x) => x.trim()).filter(Boolean);

/** Keeps only destinations the journey allows: students search by country, annual cover by region. */
export function destinationsFor(journey, codes) {
  const allowed = codes.filter((c) => (journey === 'student' ? !isRegion(c) : journey === 'annual' ? isRegion(c) : true));
  if (allowed.length) return allowed;
  return journey === 'student' ? ['GB'] : ['ASI'];
}

export function agesFor(journey, ages) {
  const { min, max } = ageLimits(journey);
  return ages.map((a) => Math.min(max, Math.max(min, a)));
}

/** Search values from URL query parameters, with defaults for anything missing. */
export function parseInsuranceQuery(q) {
  const journey = journeys.some(([k]) => k === q.journey) ? q.journey : 'single';
  const start = q.start || addDaysIso(7);
  const ages = list(q.ages).map(Number).filter((n) => Number.isInteger(n));
  return {
    journey,
    dest: destinationsFor(journey, list(q.dest).map((c) => c.toUpperCase())),
    start,
    end: q.end && q.end >= start ? q.end : addDaysIso(7, start),
    cover: studentCover.some(([k]) => k === q.cover) ? q.cover : '365',
    maxTrip: annualTripLengths.some(([k]) => k === q.maxTrip) ? q.maxTrip : '45',
    ages: (ages.length ? ages : [30]).slice(0, MAX_TRAVELLERS),
  };
}

/** URL query values for a search; only the fields its journey uses. */
export function toInsuranceParams(s) {
  return {
    journey: s.journey,
    dest: s.dest.join(','),
    start: s.start,
    ...(s.journey === 'single' ? { end: s.end } : {}),
    ...(s.journey === 'student' ? { cover: s.cover } : {}),
    ...(s.journey === 'annual' ? { maxTrip: s.maxTrip } : {}),
    ages: s.ages.join(','),
  };
}

export function toSearchRequest(s) {
  return {
    journeyType: journeyType[s.journey],
    startDate: s.start,
    ...(s.journey === 'single' ? { endDate: s.end } : {}),
    ...(s.journey === 'student' ? { coverageDays: Number(s.cover) } : {}),
    ...(s.journey === 'annual' ? { maxTripDays: Number(s.maxTrip) } : {}),
    destinations: s.dest.map((code) => ({ code, type: isRegion(code) ? 'Region' : 'Country' })),
    travellerAges: s.ages,
  };
}

/** Last day of cover. */
export function coverEnd(s) {
  if (s.journey === 'single') return s.end;
  const d = parseIso(s.start);
  if (s.journey === 'annual') d.setFullYear(d.getFullYear() + 1);
  else d.setDate(d.getDate() + Number(s.cover));
  d.setDate(d.getDate() - 1);
  return toIso(d);
}

export const coverDays = (start, end) => daysBetween(start, end) + 1;
export const destinationLabel = (codes) => codes.map(destinationName).join(', ');
export const agesLabel = (ages) => `${ages.length} traveller${ages.length === 1 ? '' : 's'} · age${ages.length === 1 ? '' : 's'} ${ages.join(', ')}`;

export function durationLabel(s) {
  if (s.journey === 'student') return (studentCover.find(([k]) => k === s.cover) || [])[1];
  if (s.journey === 'annual') return `1 year · trips ${(annualTripLengths.find(([k]) => k === s.maxTrip) || [])[1]?.toLowerCase()}`;
  const days = coverDays(s.start, s.end);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export const datesLabel = (s) => `${fmtDate(s.start)} – ${fmtDate(coverEnd(s))}`;

/** Problems the API would reject, caught before searching. */
export function searchProblem(s) {
  if (!s.dest.length) return 'Choose where you are travelling.';
  if (s.journey === 'single' && coverDays(s.start, s.end) > MAX_TRIP_DAYS) {
    return `Single-trip cover lasts up to ${MAX_TRIP_DAYS} days. For longer stays choose Student or Annual multi-trip.`;
  }
  const { min, max } = ageLimits(s.journey);
  if (s.ages.some((a) => a < min || a > max)) {
    return s.journey === 'student'
      ? 'Student cover is for travellers aged 18 to 45.'
      : 'Travellers must be 70 or younger to buy online.';
  }
  return null;
}
